import { useTranslation } from 'react-i18next';
import { EmptyState, PageSpinner } from '@/components/common';
import { FundamentalsCatalogGrid } from '@/components/common/FundamentalsCatalog';
import { useFundamentals } from '@/hooks/useFundamentals';
import { fundamentalsLogoSrc } from './fundamentalsLogo';
import './FundamentalsView.css';

/**
 * Estado de error de la carga del catálogo. Reutiliza las claves genéricas
 * `common.errorGeneric` / `common.retry` en vez de sumar claves nuevas a los
 * 7 locales: el mensaje no necesita ser específico de Fundamentales, y así se
 * evita tocar `src/i18n/locales/*` mientras otra sesión los edita en paralelo.
 */
function FundamentalsErrorState({ onRetry, isRetrying }: { onRetry: () => void; isRetrying: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="fund-view-error" role="alert">
      <div className="fund-view-error-icon" aria-hidden="true">
        <i className="fas fa-triangle-exclamation" />
      </div>
      <p className="fund-view-error-desc">{t('common.errorGeneric')}</p>
      <button type="button" className="fund-view-error-retry" onClick={onRetry} disabled={isRetrying}>
        <i className="fas fa-rotate-right" aria-hidden="true" />
        {t('common.retry')}
      </button>
    </div>
  );
}

export function FundamentalsView() {
  const { t, i18n } = useTranslation();
  const catalogQuery = useFundamentals();
  const lang = i18n.language.split('-')[0];

  if (catalogQuery.isLoading) return <PageSpinner />;

  return (
    <div className="fund-view">
      <header className="fund-view-header">
        {/* El título es el logo rotulado. El `h1` se conserva para que el
            encabezado siga existiendo como tal, con el nombre en el `alt`. */}
        <h1 className="fund-view-title">
          <img
            src={fundamentalsLogoSrc(lang)}
            alt={t('fundamentals.title')}
            className="fund-view-logo"
          />
        </h1>
        <p className="fund-view-subtitle">{t('fundamentals.subtitle')}</p>
      </header>
      {catalogQuery.isError && (
        <FundamentalsErrorState
          onRetry={() => catalogQuery.refetch()}
          isRetrying={catalogQuery.isFetching}
        />
      )}
      {!catalogQuery.isError && (catalogQuery.data?.length ?? 0) === 0 && (
        <div className="fund-view-empty">
          <EmptyState icon="fa-compass" message={t('analytics.noData')} />
        </div>
      )}
      {!catalogQuery.isError && (catalogQuery.data?.length ?? 0) > 0 && (
        <FundamentalsCatalogGrid catalogData={catalogQuery.data ?? []} lang={lang} />
      )}
    </div>
  );
}
